/**
 * @fileoverview Agent message component for rendering query and helper messages
 * 
 * @purpose Renders query and helper agent messages with appropriate UI styling
 * 
 * @usage Used by KorvoChat to display agent messages for admin users
 * 
 * @ownership KorvoChat component infrastructure
 * 
 * @dependencies 
 * - RagResultsCollapsible component
 * 
 * @restrictions 
 * - Only renders for admin users (checked by parent component)
 * - Server already validated admin status before sending agent messages
 */

import React from 'react';

import type { ChatMessage } from '../types';
import { RagResultsCollapsible } from './RagResultsCollapsible';

export type AgentMessageRagItem = {
  content: string
  title?: string
  similarity?: number
  sourceId?: string | number
}

/** Flags and optional RAG payload sometimes attached to agent-role messages. */
export type AgentMessageExtension = {
  isQueryMessage?: boolean
  isHelperMessage?: boolean
  ragResults?: AgentMessageRagItem[]
}

export type AgentMessageProps = {
  message: ChatMessage & AgentMessageExtension
  onShowDebugInfo: (messageId: string) => void;
  showTimestamps?: boolean;
  formatTimestamp: (timestamp: number) => string;
}

/**
 * Determines if a message is an agent message (query or helper)
 */
function asAgentMessageExtension(message: ChatMessage): ChatMessage & AgentMessageExtension {
  return message as ChatMessage & AgentMessageExtension
}

export function isAgentMessage(message: ChatMessage): boolean {
  const m = asAgentMessageExtension(message)
  return (
    m.isHelperMessage === true ||
    message.role === 'helper' ||
    m.isQueryMessage === true ||
    message.role === 'query'
  )
}

/**
 * Gets the agent type from a message
 */
export function getAgentType(message: ChatMessage): 'query' | 'helper' {
  return message.role === 'query' ? 'query' : 'helper';
}

/**
 * AgentMessage component for rendering query and helper messages
 */
export function AgentMessage({
  message,
  onShowDebugInfo,
  showTimestamps = false,
  formatTimestamp,
}: AgentMessageProps) {
  const agentType = getAgentType(message)
  const ragResults = asAgentMessageExtension(message).ragResults

  return (
    <div
      key={message.id}
      className="flex justify-start group"
      data-role={agentType}
      data-message-role={agentType}
    >
      <div className="flex items-start space-x-2 max-w-xs lg:max-w-md">
        <div className="flex-shrink-0 mt-1">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center ${
              agentType === 'query'
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground'
            }`}
          >
            <span className="text-xs font-semibold">{agentType === 'query' ? 'Q' : 'H'}</span>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="px-3 py-2 rounded-lg bg-background-secondary border border-border-default">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-foreground-default">
                {agentType === 'query' ? 'Query Agent' : 'Helper Agent'}
              </span>
              <span className="text-xs text-foreground-muted" aria-hidden>
                •
              </span>
              <span className="text-xs text-foreground-muted">Debug</span>
              {/* Info button for agent messages */}
              <button
                onClick={() => {
                  void onShowDebugInfo(message.id);
                }}
                className="ml-auto px-2 py-1 text-xs rounded border border-border-default bg-background-default text-foreground-default hover:bg-background-muted"
                title="Show debug info"
                aria-label="Show debug info"
              >
                ℹ️
              </button>
            </div>
            <div className="text-sm text-foreground-default whitespace-pre-wrap">
              {message.content}
            </div>
          </div>
          {/* RAG Results (collapsible) - only for query messages */}
          {agentType === 'query' &&
            ragResults &&
            Array.isArray(ragResults) &&
            ragResults.length > 0 && (
              <RagResultsCollapsible ragResults={ragResults} messageId={message.id} />
            )}
          {showTimestamps && (
            <p className="korvochat-timestamp text-xs mt-1 text-foreground-muted">
              {formatTimestamp(message.timestamp)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

